import React, { createContext, useContext, useState, useRef, useEffect, ReactNode } from 'react';

export type SongItem = {
  id: string;
  title: string;
  artist: string;
  duration: number | string;
  src: string;
  sourceType?: 'local' | 'cloud' | 'generated';
  mimeType?: string;
  fileName?: string;
  remotePath?: string;
};

type MusicContextType = {
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
  currentTrackIndex: number;
  setCurrentTrackIndex: (index: number) => void;
  audioRef: React.RefObject<HTMLAudioElement>;
  tracks: string[];
  currentSrc: string;
  setCurrentSrc: (src: string) => void;
  library: SongItem[];
  setLibrary: (songs: SongItem[]) => void;
  playlist: SongItem[];
  setPlaylist: (songs: SongItem[]) => void;
  activeId: string | null;
  setActiveId: (id: string | null) => void;
  shuffleAndPlay: () => void;
  // Radio/Stream support
  streamUrl: string | null;
  setStreamUrl: (url: string | null) => void;
  isRadioMode: boolean;
  setIsRadioMode: (isRadio: boolean) => void;
  connectToStream: (url: string) => void;
  disconnectStream: () => void;
  startRadioPlayback: (opts?: { forceReload?: boolean }) => Promise<void>;
  stopPlayback: () => void;
};

const MusicContext = createContext<MusicContextType | undefined>(undefined);

export function MusicProvider({ children }: { children: ReactNode }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentSrc, setCurrentSrc] = useState<string>("");
  const [library, setLibrary] = useState<SongItem[]>([]);
  const [playlist, setPlaylist] = useState<SongItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const libraryRef = useRef<SongItem[]>([]);
  
  // Radio/Stream support
  // In dev, route through the Vite proxy (/stream-radio) so the browser sees
  // same-origin and the Web Audio AnalyserNode can read real frequency data.
  // In production, use the direct URL (configure a proxy on your host if needed).
  const AZURACAST_STREAM_URL = import.meta.env.DEV
    ? '/stream-radio'
    : 'https://radio.minibid.org/listen/minibid/radio.mp3';
  const [streamUrl, setStreamUrl] = useState<string | null>(AZURACAST_STREAM_URL);
  const [isRadioMode, setIsRadioMode] = useState(true);

  // For live streams, only add cache-busting when forced reload AND the param doesn't already exist
  // This prevents aggressive TCP reconnection that causes microcutters
  const createFreshStreamUrl = (url: string, forceNew: boolean = false) => {
    // If already has nocache param, return as-is to avoid multiple nocache params
    if (url.includes('nocache=')) {
      return url;
    }
    // Only add nocache for forced reloads, not for every playback attempt
    if (forceNew) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}nocache=${Date.now()}`;
    }
    return url;
  };

  const toComparableUrl = (url: string) => {
    if (!url) return '';
    if (typeof window === 'undefined') return url;
    try {
      return new URL(url, window.location.href).href;
    } catch {
      return url;
    }
  };

  const assignAudioSrcIfNeeded = (audio: HTMLAudioElement, nextSrc: string) => {
    const currentComparable = toComparableUrl(audio.currentSrc || audio.src || '');
    const nextComparable = toComparableUrl(nextSrc);
    if (currentComparable === nextComparable) {
      return false;
    }
    audio.src = nextSrc;
    return true;
  };

  const tracks = [
    '/faye/track1.mp3',
    '/faye/track2.mp3',
    '/faye/track3.mp3',
    '/faye/track4.mp3',
    '/faye/track5.mp3',
  ];

  useEffect(() => {
    return () => {
      // cleanup on unmount
    };
  }, []);

  // Actualizar libraryRef cuando cambia library
  useEffect(() => {
    console.log('[MusicContext] Library updated:', library.length, 'songs');
    libraryRef.current = library;
  }, [library]);

  // Guardar estado de reproducción a localStorage
  useEffect(() => {
    if (playlist.length > 0 && activeId) {
      try {
        const state = {
          playlist,
          activeId,
          currentSrc,
          isPlaying,
        };
        localStorage.setItem('faye-playback-state', JSON.stringify(state));

      } catch (err) {
        console.error('[MusicContext] Failed to save playback state:', err);
      }
    }
  }, [playlist, activeId, currentSrc, isPlaying]);

  // Actualizar el src del elemento audio cuando currentSrc cambia
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentSrc) return;
    
    console.log('[MusicContext] Updating audio.src to:', currentSrc.substring(0, 50));
    assignAudioSrcIfNeeded(audio, currentSrc);
  }, [currentSrc]);

  // Actualizar track cuando cambia el índice
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.src = tracks[currentTrackIndex];
  }, [currentTrackIndex]);

  // Controlar play/pause - solo reacciona a isPlaying, NO a currentSrc
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      console.log('[MusicContext] useEffect play/pause: audioRef.current is null');
      return;
    }
    
    console.log('[MusicContext] useEffect play/pause triggered: isPlaying=', isPlaying, 'audio.src=', audio.src?.substring(0, 50));
    
    if (isPlaying) {
      console.log('[MusicContext] Calling audio.play()');
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('[MusicContext] audio.play() succeeded');
          })
          .catch((err) => {
            console.error('[MusicContext] audio.play() failed:', err);
            setIsPlaying(false);
          });
      }
    } else {
      console.log('[MusicContext] Calling audio.pause()');
      audio.pause();
    }
  }, [isPlaying]);

  // Monitor for stream errors and auto-reconnect for live radio
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isRadioMode) return;

    let reconnectTimeout: NodeJS.Timeout | null = null;

    const handleError = () => {
      console.warn('[MusicContext] Audio error detected, will attempt reconnect in 3s');
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      reconnectTimeout = setTimeout(() => {
        if (isPlaying && isRadioMode) {
          console.log('[MusicContext] Auto-reconnecting radio after error');
          void startRadioPlayback({ forceReload: true });
        }
      }, 3000);
    };

    const handleStalled = () => {
      console.warn('[MusicContext] Audio stalled (no data arriving), will attempt reconnect in 5s');
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      reconnectTimeout = setTimeout(() => {
        if (isPlaying && isRadioMode) {
          console.log('[MusicContext] Auto-reconnecting radio after stall');
          void startRadioPlayback({ forceReload: true });
        }
      }, 5000);
    };

    const handleSuspend = () => {
      console.warn('[MusicContext] Audio suspended (download paused)');
      // Less aggressive than stalled - only reconnect if still playing in 10s
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      reconnectTimeout = setTimeout(() => {
        if (isPlaying && isRadioMode && audio.readyState === 0) {
          console.log('[MusicContext] Auto-reconnecting radio after prolonged suspend');
          void startRadioPlayback({ forceReload: true });
        }
      }, 10000);
    };

    const handleAbort = () => {
      console.warn('[MusicContext] Audio aborted, reconnecting immediately');
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (isPlaying && isRadioMode) {
        void startRadioPlayback({ forceReload: true });
      }
    };

    audio.addEventListener('error', handleError);
    audio.addEventListener('stalled', handleStalled);
    audio.addEventListener('suspend', handleSuspend);
    audio.addEventListener('abort', handleAbort);

    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('stalled', handleStalled);
      audio.removeEventListener('suspend', handleSuspend);
      audio.removeEventListener('abort', handleAbort);
    };
  }, [isRadioMode, isPlaying]);

  // Keep-alive: detect if audio paused unexpectedly and resume
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isRadioMode || !isPlaying) return;

    const keepAlive = setInterval(() => {
      // If we expect to be playing but audio is paused (unexpected pause)
      if (isPlaying && audio.paused) {
        console.warn('[MusicContext] Keep-alive: Audio unexpectedly paused, resuming');
        audio.play().catch((err) => {
          console.error('[MusicContext] Keep-alive: Failed to resume:', err);
        });
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(keepAlive);
  }, [isRadioMode, isPlaying]);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isRadioMode || !isPlaying) return;

    let lastTime = audio.currentTime;
    let stallCounter = 0;

    const heartbeat = setInterval(() => {
      // For live streams, currentTime doesn't advance, but buffered data should increase
      // If buffered hasn't changed and we've been waiting, assume frozen
      const bufferedLength = audio.buffered.length;
      const hasBufferedData = bufferedLength > 0;
      const currentBuffered = hasBufferedData ? audio.buffered.end(bufferedLength - 1) : 0;

      // If we're playing but no new data buffered for 30s, assume frozen
      if (audio.readyState >= 2 && !isNaN(currentBuffered)) {
        stallCounter++;
        if (stallCounter > 6) {
          // 30 seconds of no progress
          console.warn('[MusicContext] Heartbeat: Stream frozen (no data for 30s), reconnecting');
          clearInterval(heartbeat);
          void startRadioPlayback({ forceReload: true });
          return;
        }
      } else {
        stallCounter = 0;
      }

      lastTime = audio.currentTime;
    }, 5000); // Check every 5 seconds

    return () => clearInterval(heartbeat);
  }, [isRadioMode, isPlaying]);

  // Pasar a la siguiente canción cuando termina la actual
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setCurrentTrackIndex((prev) => (prev + 1) % tracks.length);
    };
    
    const handlePause = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('play', handlePlay);
    
    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('play', handlePlay);
    };
  }, [tracks.length]);

  const shuffleAndPlay = () => {
    console.log('[MusicContext] shuffleAndPlay called, library length:', libraryRef.current.length);
    if (libraryRef.current.length === 0) {
      console.log('[MusicContext] Library is empty, waiting for library to load...');
      // Esperar a que la librería se cargue (máximo 2 segundos)
      const checkLibrary = setInterval(() => {
        console.log('[MusicContext] Checking library, current length:', libraryRef.current.length);
        if (libraryRef.current.length > 0) {
          clearInterval(checkLibrary);
          console.log('[MusicContext] Library loaded with', libraryRef.current.length, 'songs');
          performShuffle();
        }
      }, 100);
      
      setTimeout(() => {
        clearInterval(checkLibrary);
        if (libraryRef.current.length === 0) {
          console.log('[MusicContext] Library still empty after 2 seconds');
        }
      }, 2000);
      return;
    }
    performShuffle();
  };

  const performShuffle = () => {
    const shuffled = [...libraryRef.current].sort(() => Math.random() - 0.5);
    console.log('[MusicContext] Shuffled', shuffled.length, 'songs');
    setPlaylist(shuffled);
    setActiveId(shuffled[0].id);
    setCurrentSrc(shuffled[0].src);
    
    // Setear el src directamente en el elemento de audio para que sea inmediato
    if (audioRef.current) {
      audioRef.current.src = shuffled[0].src;
    }
    
    setIsPlaying(true);
  };

  const connectToStream = (url: string) => {
    console.log('[MusicContext] Connecting to stream:', url);
    setStreamUrl(url);
    setIsRadioMode(true);
    void startRadioPlayback({ forceReload: true });
  };

  const disconnectStream = () => {
    console.log('[MusicContext] Disconnecting from stream');
    setStreamUrl(null);
    setIsRadioMode(false);
    stopPlayback();
  };

  const stopPlayback = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
    setIsPlaying(false);
  };

  const startRadioPlayback = async (opts?: { forceReload?: boolean }) => {
    const baseUrl = streamUrl || AZURACAST_STREAM_URL;
    // Only bypass cache when explicitly forced (e.g., quality change).
    // Prevents aggressive TCP reconnection that causes microcutters in live streams.
    const nextUrl = createFreshStreamUrl(baseUrl, opts?.forceReload ?? false);

    setIsRadioMode(true);
    setStreamUrl(baseUrl);
    setCurrentSrc(nextUrl);

    const audio = audioRef.current;
    if (!audio) {
      setIsPlaying(true);
      return;
    }

    try {
      // Check if we need to change the source
      const srcChanged = assignAudioSrcIfNeeded(audio, nextUrl);
      
      // Soft reset: only pause and reset position, don't hard-reset the element
      // This preserves existing TCP connection for better stream continuity
      if (audio.paused === false) {
        audio.pause();
      }
      
      // Reset playback position for clean restart
      try {
        audio.currentTime = 0;
      } catch (timeErr) {
        console.warn('[MusicContext] Failed to reset currentTime:', timeErr);
      }
      
      // Only call load() if source changed to avoid unnecessary buffer reset
      if (srcChanged) {
        try {
          audio.load();
        } catch (loadErr) {
          console.warn('[MusicContext] Failed to load new source:', loadErr);
        }
      }
      
      // Small delay to ensure state is consistent before playing
      await new Promise(resolve => setTimeout(resolve, 50));
      
      await audio.play();
      setIsPlaying(true);
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        try {
          await audio.play();
          setIsPlaying(true);
          return;
        } catch (retryError) {
          console.error('[MusicContext] startRadioPlayback retry failed:', retryError);
        }
      }
      console.error('[MusicContext] startRadioPlayback failed:', err);
      setIsPlaying(false);
    }
  };

  return (
    <MusicContext.Provider value={{ isPlaying, setIsPlaying, currentTrackIndex, setCurrentTrackIndex, audioRef, tracks, currentSrc, setCurrentSrc, library, setLibrary, playlist, setPlaylist, activeId, setActiveId, shuffleAndPlay, streamUrl, setStreamUrl, isRadioMode, setIsRadioMode, connectToStream, disconnectStream, startRadioPlayback, stopPlayback }}>
      <audio ref={audioRef} src={currentSrc} preload="auto" crossOrigin="anonymous" />
      {children}
    </MusicContext.Provider>
  );
}

export function useMusic() {
  const context = useContext(MusicContext);
  if (!context) {
    throw new Error('useMusic must be used within a MusicProvider');
  }
  return context;
}
